import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
    DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
    pointerWithin, rectIntersection, closestCenter, getFirstCollision, useDroppable,
    MeasuringStrategy, TraversalOrder,
} from '@dnd-kit/core';
import {
    SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FaGripVertical, FaTimes, FaLayerGroup, FaChevronDown } from 'react-icons/fa';

const BRAND_NAVY   = '#1f3c88';
const BRAND_BLUE   = '#2d7ff9';
const BRAND_SKY    = '#eef6ff';
const BRAND_BORDER = '#dbe8f8';
// Quanto o ponteiro pode passar da borda de uma coluna e ela ainda rolar sozinha. Pouco mais que
// meio chip: empurrar um dedo além da borda é o gesto de quem quer continuar rolando; atravessar a
// tela para outro agrupamento, não.
const MARGEM_ROLAGEM = 24;

// Mapa grupoId → [itemId] e índice itemId → item, a partir das props.
const mapaDe = (grupos) => Object.fromEntries((grupos ?? []).map(g => [String(g.id), g.itens.map(i => String(i.id))]));
const infoDe = (grupos) => {
    const m = {};
    (grupos ?? []).forEach(g => g.itens.forEach(i => { m[String(i.id)] = i; }));
    return m;
};

/**
 * Board de containers (dnd-kit, padrão "multiple containers"). Mantém a ordem
 * LOCALMENTE durante o arraste (mover entre grupos ao vivo, no onDragOver) p/ a
 * animação ficar suave — igual ao modal Ajustar (ColunaArrastavel). Ao soltar,
 * emite o layout final num único callback; a página reconstrói as opções.
 *
 * @param {Array}    grupos        [{ id, label, badge?, itens: [{ id, label|conteudo, cor?, removivel? }] }]
 * @param {Function} onReorganizar (mapa: { grupoId: [itemId] }) => void — layout final ao soltar
 * @param {Function} [onRemover]   (itemId) => void — mostra o X no chip (oculto se item.removivel === false)
 * @param {Function} [onAbrirItem] (itemId) => void — dois cliques no chip. Dois, e não um: o chip inteiro
 *                                 é alça de arraste, e pegar e soltar sem mover já é um clique
 */
export default function AgrupamentoBoard({ grupos, onReorganizar, onRemover, onAbrirItem }) {
    const [mapa, setMapa]   = useState(() => mapaDe(grupos));
    const [info, setInfo]   = useState(() => infoDe(grupos));
    const [ativoId, setAtivoId] = useState(null);
    // Onde a última soltura caiu, para a coluna recolhida que recebeu o item se abrir sozinha:
    // guardar só o id não bastaria, porque soltar duas vezes na mesma coluna daria o mesmo valor e
    // a segunda não reabriria se ela tivesse sido fechada no meio. O carimbo muda sempre.
    const [soltouEm, setSoltouEm] = useState(null);
    const arrastando = useRef(false);
    const ultimoOver = useRef(null);
    // Onde o ponteiro está, para o `podeRolar` abaixo. Vem do detector de colisão, que é chamado a
    // cada movimento e já recebe a coordenada pronta.
    const ponteiro = useRef(null);

    // Fora do arraste, a ordem segue as props; durante o arraste é local (otimista).
    useEffect(() => {
        if (arrastando.current) return;
        setMapa(mapaDe(grupos));
        setInfo(infoDe(grupos));
    }, [grupos]);

    // Toque: segura 50ms para arrastar (touch-action em custom.css). Mouse: 5px, como antes —
    // com delay, o dnd-kit cancelaria o arraste rápido que passa de 5px antes da espera.
    const sensores = useSensors(useSensor(PointerSensor, {
        activationConstraint: window.matchMedia('(pointer: coarse)').matches
            ? { delay: 50, tolerance: 5 }
            : { distance: 5 },
    }));

    // grupoId de um id (o próprio id se for um grupo, senão o grupo que o contém).
    const containerDe = useCallback((id) => {
        const s = String(id);
        if (mapa[s]) return s;
        return Object.keys(mapa).find(gid => mapa[gid].includes(s)) ?? null;
    }, [mapa]);

    // Colisão canônica p/ múltiplos containers: ponteiro primeiro; se cair num grupo
    // com itens, mira o item mais próximo dentro dele (o SortableContext abre o espaço
    // e os chips deslizam). Guarda o último alvo p/ não "piscar" ao sair da área.
    const colisao = useCallback((args) => {
        ponteiro.current = args.pointerCoordinates ?? null;
        const inter = pointerWithin(args).length ? pointerWithin(args) : rectIntersection(args);
        let overId = getFirstCollision(inter, 'id');
        if (overId != null) {
            if (mapa[overId] && mapa[overId].length > 0) {
                const dentro = args.droppableContainers.filter(
                    c => String(c.id) !== String(overId) && mapa[overId].includes(String(c.id)));
                overId = closestCenter({ ...args, droppableContainers: dentro })[0]?.id ?? overId;
            }
            ultimoOver.current = overId;
            return [{ id: overId }];
        }
        return ultimoOver.current != null ? [{ id: ultimoOver.current }] : [];
    }, [mapa]);

    const aoIniciar = (e) => { arrastando.current = true; setAtivoId(String(e.active.id)); };

    // Move o item entre grupos AO VIVO (animação suave de troca de container).
    const aoArrastar = (e) => {
        const { active, over } = e;
        if (!over) return;
        const cAtivo = containerDe(active.id);
        const cOver  = containerDe(over.id);
        if (!cAtivo || !cOver || cAtivo === cOver) return;
        setMapa(prev => {
            const semAtivo = prev[cAtivo].filter(id => id !== String(active.id));
            const overArr  = prev[cOver];
            // índice de inserção: sobre um item → na posição dele; sobre o grupo → no fim.
            const i = prev[over.id] ? overArr.length : overArr.indexOf(String(over.id));
            const idx = i >= 0 ? i : overArr.length;
            return { ...prev, [cAtivo]: semAtivo, [cOver]: [...overArr.slice(0, idx), String(active.id), ...overArr.slice(idx)] };
        });
    };

    const aoSoltar = (e) => {
        const { active, over } = e;
        arrastando.current = false;
        setAtivoId(null);
        ultimoOver.current = null;
        if (!over) { setMapa(mapaDe(grupos)); return; }
        const cOver = containerDe(over.id);
        if (!cOver) { setMapa(mapaDe(grupos)); return; }
        setSoltouEm({ id: cOver, em: Date.now() });

        let finalMapa = mapa;
        if (!mapa[over.id]) { // soltou sobre um item → reordena dentro do grupo
            const arr  = mapa[cOver];
            const de   = arr.indexOf(String(active.id));
            const para = arr.indexOf(String(over.id));
            if (de >= 0 && para >= 0 && de !== para) {
                finalMapa = { ...mapa, [cOver]: arrayMove(arr, de, para) };
                setMapa(finalMapa);
            }
        }
        onReorganizar && onReorganizar(finalMapa);
    };

    const aoCancelar = () => {
        arrastando.current = false;
        setAtivoId(null);
        ultimoOver.current = null;
        setMapa(mapaDe(grupos));
    };

    const itemAtivo = ativoId ? info[ativoId] : null;

    // Uma caixa só rola quando o ponteiro está EM CIMA dela. O dnd-kit compara a posição do ponteiro
    // com as bordas da caixa sem exigir que ele esteja dentro: um ponteiro ACIMA do topo satisfaz
    // "chegou na faixa de cima" do mesmo jeito que um ponteiro logo abaixo dela. Saindo de um
    // agrupamento para outro, o item é movido para a coluna de destino antes de o ponteiro chegar
    // nela — e a coluna, ainda lá embaixo, começava a rolar sozinha por causa de um ponteiro que
    // estava longe. A página segue livre: é ela que leva o item até uma coluna fora da vista.
    const podeRolar = useCallback((el) => {
        const p = ponteiro.current;
        if (!p) return true;
        if (el === document.scrollingElement || el === document.documentElement || el === document.body) return true;
        const r = el.getBoundingClientRect();
        // Uma margem de tolerância para fora: empurrar um pouco além da borda é o gesto natural de
        // quem quer continuar rolando, e exigir o ponteiro estritamente dentro cortaria a rolagem
        // justamente no fim do movimento.
        const m = MARGEM_ROLAGEM;
        return p.x >= r.left - m && p.x <= r.right + m && p.y >= r.top - m && p.y <= r.bottom + m;
    }, []);

    const rolagemAutomatica = useMemo(() => ({
        // O padrão do dnd-kit (`TreeOrder`) INVERTE a lista de ancestrais roláveis e percorre do mais
        // externo para o mais interno, parando no primeiro que aceita rolar. Com a página rolável,
        // ela ganha sempre e a coluna nunca é alcançada: o que descia era a tela, não a caixa.
        order: TraversalOrder.ReversedTreeOrder,
        // x no padrão. Zero NÃO desliga o eixo: o cálculo divide pela faixa, e faixa zero dá
        // velocidade infinita, que é aceita antes do eixo Y e trava a rolagem vertical.
        // y: ~58px dos 266 — pouco mais de um chip em cima e embaixo: dá para pousar no meio sem
        // disparar, e a borda é fácil de alcançar.
        threshold: { x: 0.2, y: 0.22 },
        // BEM abaixo do padrão (10): a velocidade é `aceleração × quanto se entrou na faixa`, em
        // pixels, a cada 5ms — no padrão a caixa de 266 passava inteira num piscar.
        acceleration: 2,
        canScroll: podeRolar,
    }), [podeRolar]);

    return (
        // `Always`: por padrão o dnd-kit mede as zonas uma vez, quando o arraste começa. A coluna
        // recolhida CRESCE no meio do caminho (abre a faixa de soltura ao receber o ponteiro), e com
        // a medida velha o ponteiro que desce até a faixa cairia fora dela — a faixa fecharia, o
        // alvo viraria a coluna de baixo, e a faixa piscaria sem deixar soltar.
        // O corpo da coluna mostra cinco chips e guarda o resto atrás da rolagem, então reordenar
        // para uma posição fora da vista depende de a caixa rolar sozinha com o item no ar. A
        // configuração disso está em `rolagemAutomatica`, acima.
        <DndContext sensors={sensores} collisionDetection={colisao}
            measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
            autoScroll={rolagemAutomatica}
            onDragStart={aoIniciar} onDragOver={aoArrastar} onDragEnd={aoSoltar} onDragCancel={aoCancelar}>
            <div className="d-flex flex-wrap gap-3">
                {grupos.map(g => (
                    <Grupo key={g.id} id={String(g.id)} label={g.label} badge={g.badge}
                        tipoBadge={g.tipoBadge} acao={g.acao} arrastando={!!ativoId}
                        abertoInicial={!!g.abertoInicial}
                        recebeuSolta={soltouEm && soltouEm.id === String(g.id) ? soltouEm.em : null}
                        itemIds={mapa[String(g.id)] ?? []} info={info} onRemover={onRemover} onAbrirItem={onAbrirItem} />
                ))}
            </div>
            <DragOverlay>{itemAtivo ? <Chip item={itemAtivo} overlay /> : null}</DragOverlay>
        </DndContext>
    );
}

function Grupo({ id, label, tipoBadge, badge, acao, itemIds, info, onRemover, onAbrirItem, arrastando,
    recebeuSolta, abertoInicial }) {
    const { setNodeRef, isOver } = useDroppable({ id, data: { tipo: 'grupo' } });
    // Nasce fechada: o board tem uma coluna por linha e, todas abertas, a página vira uma rolagem
    // só. Fechadas, a lista de colunas cabe de uma vez e quem abre é quem vai mexer. A exceção é
    // quem declara `abertoInicial` — hoje só as Órfãs, e só quando têm item.
    const [aberto, setAberto] = useState(abertoInicial);
    // Recebeu um item: abre. Recolhida, o item entraria e sumiria de vista — quem acabou de soltar
    // precisa ver onde a coisa caiu, e quase sempre o passo seguinte é reordenar ali dentro.
    useEffect(() => { if (recebeuSolta) setAberto(true); }, [recebeuSolta]);
    return (
        // Uma coluna por linha. Lado a lado, o que sobra para o nome do grupo depois dos badges e
        // da ação é pouco, e o título truncava antes de o conteúdo apertar — o texto do cabeçalho
        // é o que mais pede largura aqui, não o chip. Ocupando a linha toda ele cabe inteiro em
        // qualquer tela, e a altura é que passa a ser o limite (o `maxHeight` abaixo).
        // Recolhida, quem recebe a soltura é o CARD INTEIRO, não a faixa: a faixa só nasce depois
        // que o ponteiro chega, e se ela fosse a única zona não haveria como chegar nela. Aberta,
        // a zona volta a ser o corpo, que é onde os itens se ordenam.
        // `overflow: hidden` recorta o corpo na curva do card: a barra de rolagem é um retângulo e
        // desce reta até a base, e sem o recorte ela vaza pelo canto arredondado.
        <div ref={aberto ? undefined : setNodeRef}
            style={{ flex: '1 1 100%', minWidth: 0, background: '#fff', overflow: 'hidden',
                border: `1px solid ${BRAND_BORDER}`, borderRadius: 14 }}>
            {/* O cabeçalho inteiro recolhe a coluna: a faixa é grande e o alvo é o card, não um
                ícone de 11px. O chevron continua existindo como botão de verdade, para dizer que
                aquilo abre e fecha e para o teclado alcançar — o clique dele não pode disparar
                duas vezes, por isso para de subir. */}
            <div className="d-flex align-items-center gap-2 px-3 py-2"
                onClick={() => setAberto(a => !a)}
                style={{ borderBottom: `1px solid ${BRAND_SKY}`, cursor: 'pointer', userSelect: 'none' }}>
                <FaLayerGroup size={12} style={{ color: BRAND_BLUE, flexShrink: 0 }} />
                <span className="fw-bold text-truncate" style={{ color: BRAND_NAVY, fontSize: 13 }}>{label}</span>
                {/* Mesma ordem do modo lista: o que a coluna É fica colado no nome,
                    o estado e a ação vão para a direita. */}
                {/* Encolhe, ao contrário dos badges da direita: aqui dentro vai a observação, que
                    leva texto e trunca sozinha. Fixo, ela empurrava o nome do grupo para fora. */}
                {tipoBadge && <span style={{ minWidth: 0, display: 'flex' }}>{tipoBadge}</span>}
                <span className="ms-auto d-flex align-items-center gap-2" style={{ flexShrink: 0 }}>
                    {badge}
                    {/* O lápis abre a edição. O clique dele para aqui: senão editar recolheria junto. */}
                    {acao && <span className="d-flex align-items-center" onClick={e => e.stopPropagation()}>{acao}</span>}
                    <button type="button" aria-expanded={aberto} title={aberto ? 'Recolher' : 'Expandir'}
                        className="btn btn-sm p-0 border-0 bg-transparent d-flex align-items-center"
                        style={{ color: BRAND_NAVY, opacity: 0.55 }}
                        onClick={e => { e.stopPropagation(); setAberto(a => !a); }}>
                        <FaChevronDown size={11} style={{ transition: 'transform 140ms',
                            transform: aberto ? 'none' : 'rotate(-90deg)' }} />
                    </button>
                </span>
            </div>
            {aberto ? (
            <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
                <div ref={setNodeRef} className="p-2 d-flex flex-column gap-2"
                    // Teto de altura com rolagem PRÓPRIA: a coluna de órfãs chega a dezenas
                    // de cotações e, sem isto, estica a página inteira — o cabeçalho e as
                    // outras colunas somem de vista e não dá para arrastar entre elas.
                    // 266 = cinco chips e nada mais, o corte exato: 5 × 43,6 de chip (o botão de
                    // remover é um .btn-sm, e é ele quem manda a altura, não o texto) + 4 × 8 de vão
                    // + 2 × 8 de padding. Com cinco a caixa fecha nesta conta e não rola; o sexto
                    // fica inteiro do lado de fora, sem meia linha aparecendo embaixo.
                    style={{ minHeight: 56, maxHeight: 266, overflowY: 'auto', background: isOver ? BRAND_SKY : 'transparent', borderRadius: 12, transition: 'background 120ms' }}>
                    {itemIds.map(itemId => info[itemId] && (
                        <ItemArrastavel key={itemId} id={itemId} item={info[itemId]} onRemover={onRemover} onAbrirItem={onAbrirItem} />
                    ))}
                    {itemIds.length === 0 && (
                        <div className="text-center py-2" style={{ color: '#b9c4d6', fontSize: 12, border: `1px dashed ${BRAND_BORDER}`, borderRadius: 10 }}>
                            solte aqui
                        </div>
                    )}
                </div>
            </SortableContext>
            ) : (arrastando && isOver) ? (
                // Só quando o item está EM CIMA desta coluna recolhida: aí ela abre a faixa, que é
                // o espaço para soltar. Enquanto o item passeia pelas outras, esta continua fechada
                // — abrir todas a cada arraste faria a página inteira pular.
                <div className="p-2">
                    <div className="text-center py-2" style={{ color: '#b9c4d6', fontSize: 12,
                        border: `1px dashed ${BRAND_BORDER}`, borderRadius: 10, background: BRAND_SKY }}>
                        solte aqui
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function ItemArrastavel({ id, item, onRemover, onAbrirItem }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id, data: { tipo: 'item' },
    });
    const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
    const mostrarX = onRemover && item.removivel !== false;

    // Sem o `userSelect`, os dois cliques selecionam a palavra embaixo do ponteiro.
    return (
        <div ref={setNodeRef} style={{ ...style, cursor: isDragging ? 'grabbing' : 'grab', ...(onAbrirItem ? { userSelect: 'none' } : {}) }}
            onDoubleClick={onAbrirItem ? () => onAbrirItem(id) : undefined} {...attributes} {...listeners}>
            <Chip item={item}
                acao={mostrarX ? (
                    <button type="button" title="Remover opção"
                        className="btn btn-sm p-0 border-0 bg-transparent d-flex align-items-center"
                        style={{ color: BRAND_NAVY, opacity: 0.55 }}
                        onPointerDown={e => e.stopPropagation()}
                        onClick={e => { e.stopPropagation(); onRemover(item.id); }}>
                        <FaTimes size={13} />
                    </button>
                ) : null} />
        </div>
    );
}

// Visual do item (reusado no chip arrastável e no DragOverlay).
function Chip({ item, acao, overlay }) {
    return (
        <div className="d-flex align-items-center gap-2 px-2 py-2"
            style={{ background: BRAND_SKY, border: `1px solid ${BRAND_BORDER}`, borderRadius: 10, fontSize: 13, color: BRAND_NAVY, boxShadow: overlay ? '0 8px 20px rgba(31,60,136,0.18)' : 'none' }}>
            <FaGripVertical size={12} style={{ color: '#9fb3d4', flexShrink: 0 }} />
            {item.cor && <span style={{ width: 8, height: 8, borderRadius: '50%', background: item.cor, flexShrink: 0 }} />}
            <span className="text-truncate" style={{ minWidth: 0 }} title={item.conteudo ?? item.label}>{item.conteudo ?? item.label}</span>
            {acao && <span className="ms-auto d-flex align-items-center" style={{ flexShrink: 0 }} onPointerDown={e => e.stopPropagation()}>{acao}</span>}
        </div>
    );
}
